import { getDeviceLocale, reportError } from "@cabocheck/shared";
import IframeResizer from "@iframe-resizer/react";
import type { LoaderFunctionArgs } from "react-router";
import { Link, redirect, useLoaderData } from "react-router";
import classNames from "classnames";
import invariant from "invariant";
import { ArrowRight, Clock5, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import benefit1 from "~/assets/images/microsite/benefit1.png";
import benefit2 from "~/assets/images/microsite/benefit2.png";
import benefit3 from "~/assets/images/microsite/benefit3.png";
import benefit4 from "~/assets/images/microsite/benefit4.png";
import benefitHero1 from "~/assets/images/microsite/benefit_hero_1_replacement.jpeg";
import benefitHero2 from "~/assets/images/microsite/benefit_hero_2.jpeg";
import logoSmall from "~/assets/logos/logo-small.svg";
import { client } from "~/client/client";
import { buttonVariants } from "~/components/ui/button";
import { PublicCalendar } from "~/features/calendar/public/PublicCalendar";
import { AblaufComponent } from "~/features/microsite/Ablauf";
import { FAQComponent } from "~/features/microsite/FAQs";
import { MicrositeButton } from "~/features/microsite/MicrositeButton";
import { OpeningTimesSummary } from "~/features/microsite/OpeningTimesSummary";
import type { WeekSchedule } from "~/features/microsite/OpenningTimes";
import { parseOpeningTimes } from "~/features/microsite/OpenningTimes";
import { TestOffersComponent } from "~/features/microsite/TestOffers";
import { PublicFooter } from "~/features/public/Footer";
import { PublicPocProductBox } from "~/features/poc/PublicPocProductBox";
import { MedicalTestProfileBox } from "~/features/tests/MedicalTestProfileBox";
import { cn } from "~/utils";
import { smallerSortFirst } from "~/utils/apis/getMedicalTestProfilesList";
import { loadFAQs } from "~/utils/apis/loadFAQs";
import { getBaseParams } from "~/utils/getBaseParams";
import {
  getPartnerContractTypeSet,
  hasNonOrdersOnlyPartnerContractType,
  isVisibleForPartnerContractTypes,
} from "~/utils/partnerProductTypes";

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { slug } = params;
  invariant(slug, "slug is required");
  const { data: responseData, error } = await client.v1.getApiPartnerLandingByCode({
    ...(await getBaseParams(request, { withDeviceGuid: false })),
    code: slug,
  });

  if (error) reportError("Partner request failed", error);

  if (!responseData?.response) {
    return redirect("/");
  }

  const appointment_types_response = await client.v1.getApiPartnerAppointmentTypeList(
    responseData?.response?.partner_guid,
    {
      ...(await getBaseParams(request, { withDeviceGuid: false })),
    },
  );

  const device_locale = getDeviceLocale(request);
  const parsedOpeningTimes = parseOpeningTimes(responseData.response?.opening_time || "{}");
  const activeProductTypes = getPartnerContractTypeSet(responseData.response.active_contracts);
  const showPocProducts = hasNonOrdersOnlyPartnerContractType(activeProductTypes, "CaboPOC");
  const faqs = loadFAQs(device_locale).then((r) =>
    r.filter(
      (faq) => faq.available_for_microsite && isVisibleForPartnerContractTypes(faq.product_type, activeProductTypes),
    ),
  );

  const url = new URL(request.url);
  const appointment_types_query_param = url.searchParams.get("appointment_types");

  let appointmentTypes = appointment_types_response.data?.response?.partner_appointment_types;
  if (appointment_types_query_param) {
    const appointment_type_names = appointment_types_query_param.split(",");
    appointmentTypes = appointmentTypes?.filter((appointment_type) =>
      appointment_type_names.includes(appointment_type.name),
    );
  }

  return {
    parsedOpeningTimes,
    ...responseData.response,
    telephone: responseData.response?.telephone ?? "",
    email: responseData.response?.email ?? "",
    hero_subtitle: responseData.response?.hero_subtitle ?? "",
    image_url: responseData.response?.image_url ?? "",
    medical_test_profiles: responseData.response?.medical_test_profiles
      ?.filter((profile) => profile.selling_price != null)
      .sort(smallerSortFirst),
    faqs: await faqs,
    poc_products: showPocProducts
      ? (responseData.response?.poc_products?.filter((product) => product.selling_price != null) ?? [])
      : [],
    appointmentTypes,
  };
}

export default function PharmacyHome() {
  const { t } = useTranslation();
  const data = useLoaderData<typeof loader>();

  return (
    <div>
      <section id="start">
        <Hero
          openingTimes={data.parsedOpeningTimes}
          telephone={data.telephone}
          email={data.email}
          heroSubtitle={data.hero_subtitle}
          image={data.image_url}
        />
      </section>

      <section id="termin">
        {data?.iframe ? (
          data.show_iframe_as_url ? (
            <div className="public-container my-8">
              <h1 className="flex-1 pb-6 text-3xl font-bold tracking-tight sm:pb-12 sm:text-5xl">
                {t("Buche jetzt einen Termin")}
              </h1>
              <div className="flex justify-center">
                <a
                  href={data.iframe}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(
                    buttonVariants({ size: "lg" }),
                    "w-full max-w-xs whitespace-normal break-words text-center leading-snug sm:w-auto sm:max-w-none",
                  )}
                >
                  {t("Hier klicken um zum Terminbuchungssystem zu gelangen")}
                  <ArrowRight aria-hidden="true" className="ml-2" />
                </a>
              </div>
            </div>
          ) : (
            <div className="public-container my-8">
              <h1 className="flex-1 pb-6 text-3xl font-bold tracking-tight sm:pb-12 sm:text-5xl">
                {t("Buche jetzt einen Termin")}
              </h1>
              <div className="flex justify-center">
                <IframeResizer
                  title={t("Terminbuchung")}
                  license="GPLv3"
                  log="collapsed"
                  src={data.iframe}
                  style={{ width: "100%", height: "100vh" }}
                />
              </div>
            </div>
          )
        ) : data?.appointmentTypes?.length && data?.appointmentTypes?.length > 0 ? (
          <div className="public-container my-8">
            <h1 className="flex-1 pb-6 text-3xl font-bold tracking-tight sm:pb-12 sm:text-5xl">
              {t("Buche jetzt einen Termin")}
            </h1>
            <div className="flex justify-center">
              <PublicCalendar partnerLanding={data} initialAppointmentTypes={data.appointmentTypes} />
            </div>
          </div>
        ) : null}
      </section>
      <section id="vorteile" className="public-container">
        <h1 className="mb-6 mt-10 text-center text-3xl font-bold sm:mb-10 sm:mt-20 sm:text-6xl">
          {t("Ihre Vorteile bei der Blutanalyse in der Apotheke")}
        </h1>
        <Benefits />
        <FeatureOverview />
      </section>

      <section id="angebot">
        <TestOffersComponent
          labProducts={data.medical_test_profiles
            ?.map((p) => (
              <MedicalTestProfileBox
                key={p.medical_test_profile_guid}
                to={`products/${p.name}`}
                panel={{
                  name: p.name,
                  subline: p.subline,
                  medical_tests: p.medical_tests,
                  selling_price: p.selling_price,
                }}
              />
            ))
            .slice(0, 6)}
          pocProducts={data.poc_products?.slice(0, 6).map((product) => (
            <PublicPocProductBox
              key={product.product_guid}
              product={product}
              to={`poc-products/${product.product_guid}`}
            />
          ))}
        />
      </section>
      <section id="ablauf">
        <AblaufComponent />
      </section>
      <section id="faq">
        <FAQComponent items={data.faqs} showTypeTabs />
      </section>

      <PublicFooter />
    </div>
  );
}

function Hero({
  openingTimes,
  telephone: phone,
  email,
  heroSubtitle,
  image,
}: {
  openingTimes: WeekSchedule;
  telephone: string;
  email: string;
  heroSubtitle: string;
  image: string;
}) {
  const { t } = useTranslation();

  const infoBox = (
    <div className="bottom-2 right-2 flex w-full min-w-0 flex-col gap-3 rounded-lg bg-white p-4 font-semibold text-gray-600 shadow-xl sm:min-w-80 sm:gap-4 sm:p-6 md:absolute md:w-auto">
      <div className="flex items-center gap-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-dynamic-primary">
          <Mail aria-hidden="true" className="text-white" strokeWidth={1.5} />
        </div>
        <p className="flex-1 break-all">{email}</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex h-9 w-9 -scale-x-100 items-center justify-center rounded-lg bg-dynamic-primary">
          <Phone aria-hidden="true" className="text-white" strokeWidth={1.5} />
        </div>
        <p>{phone}</p>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-dynamic-primary">
          <Clock5 aria-hidden="true" className="text-white" strokeWidth={1.5} />
        </div>
        <OpeningTimesSummary schedule={openingTimes} />
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "-mt-24 grid overflow-hidden bg-gradient-to-r from-[--primary-color-200] to-[--secondary-color-200] pb-8 pt-32 sm:min-h-screen sm:pb-20 sm:pt-44",
      )}
    >
      <div className="public-container grid items-center justify-between gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3 md:items-start">
          <h1 className="mb-2 animate-fade-scale-in text-center text-4xl font-bold sm:mb-4 sm:mt-10 sm:text-5xl md:mt-0 md:text-left lg:text-7xl">
            {t("Gesundheit beginnt mit einem Test")}
          </h1>
          <p className="mb-3 animate-fade-scale-in whitespace-pre-line text-center text-base text-gray-500 sm:mb-6 sm:text-lg md:text-left">
            {heroSubtitle}
          </p>
          <MicrositeButton asChild>
            <Link to="#vorteile">{t("Mehr erfahren")}</Link>
          </MicrositeButton>

          <MicrositeButton asChild variant="secondary">
            <Link to="https://mein.cabocheck.com">{t("Testergebnis abrufen")}</Link>
          </MicrositeButton>
        </div>
        <div className="relative flex flex-col items-center justify-center self-stretch sm:pb-8 md:pb-0">
          <div className="relative flex p-4 md:p-4 lg:py-36">
            <img
              src={image}
              alt=""
              className="max-h-48 w-auto animate-fade-scale-in rounded-3xl object-contain sm:mb-8 sm:max-h-none"
            />
            <div className="hidden md:block">{infoBox}</div>
          </div>
          <div className="w-full md:hidden">{infoBox}</div>
        </div>
      </div>
    </div>
  );
}

function Benefits() {
  const { t } = useTranslation();

  return (
    <div className="mb-8 mt-6 grid gap-4 text-center sm:mb-16 sm:mt-10 sm:grid-cols-2 sm:gap-6 md:grid-cols-4">
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit2} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Keine lange Wartezeiten")}</h4>
      </div>
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit4} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Ergebnisse als PDF per Email oder online abrufbar")}</h4>
      </div>
      <div className="space-y-6 rounded-3xl bg-gray-50 p-4 py-8">
        <img src={benefit1} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">
          {t("Einfache Blutentnahme. Bei CaboLab mittels speziellem Unterdruckgerät.")}
        </h4>
        <b className="text-sm font-semibold">({t("nur 2 Minuten")})</b>
      </div>
      <div className="flex flex-col justify-center space-y-6 rounded-3xl bg-gray-50 p-4 py-6">
        <img src={benefit3} alt="" className="mx-auto w-16" />
        <h4 className="text-lg font-semibold">{t("Auswertung durch medizinisches Facharztlabor bei CaboLab")}</h4>
      </div>
    </div>
  );
}

function FeatureOverview() {
  const { t } = useTranslation();

  return (
    <>
      <FeatureSection
        title={t("Warum Bluttests wichtig sind")}
        image={
          <div className="self-stretch overflow-hidden rounded-3xl bg-gray-100">
            <img src={benefitHero1} alt="" className="h-full w-full rounded-t-3xl object-cover" />
          </div>
        }
      >
        <FeatureItem
          title={t("Erste Anhaltspunkte für Gesundheitsrisiken")}
          description={t(
            "Bluttests können Risikofaktoren und mögliche Erkrankungen aufzeigen, wie Blutzucker und Cholesterinspiegel.",
          )}
          icon={logoSmall}
        />
        <FeatureItem
          title={t("Wann zum Arzt gehen?")}
          description={t(
            "Bei Abweichungen der Referenzbereiche, empfehlen wir Ihnen eine Ärztliche Beratung. Medizinische Diagnosen dürfen nur durch Ärzte gestellt werden.",
          )}
          icon={logoSmall}
        />
      </FeatureSection>
      <FeatureSection
        title={t("Gesundheit und Ernährung im Einklang")}
        image={
          <div className="self-stretch overflow-hidden rounded-3xl bg-gray-100">
            <img src={benefitHero2} alt="" className="h-full w-full rounded-t-3xl object-cover" />
          </div>
        }
        isFlipped
      >
        <div className="pb-4 sm:pb-16">
          <p className="text-base text-gray-600 sm:text-lg">
            {t(
              "Eine Ernährungsumstellung ist oft der erste Schritt hin zu einer gesünderen Lebensweise. Genauso wichtig ist es, seine körperliche Gesundheit im Blick zu haben.",
            )}
          </p>
          <p className="mt-4 text-base text-gray-600 sm:mt-8 sm:text-lg">
            {t(
              "Deshalb bieten wir eine Vielzahl an Tests an, um Ihre Gesundheit umfassend zu überwachen und zu verbessern.",
            )}
          </p>
        </div>
      </FeatureSection>
    </>
  );
}

function FeatureSection({
  title,
  image,
  children,
  isFlipped,
}: {
  title: string;
  image: React.ReactNode;
  children: React.ReactNode;
  isFlipped?: boolean;
}) {
  const FeatureList = () => (
    <div className="space-y-4 py-8 sm:space-y-6 sm:py-20">
      <h2 className="text-2xl font-bold sm:text-4xl">{title}</h2>

      {children}
    </div>
  );

  return (
    <div className="mb-4 py-4 sm:mb-10 sm:px-4 sm:py-8">
      <div className={classNames("grid grid-cols-1 items-center gap-8 lg:grid-cols-2")}>
        <div className={classNames({ "lg:order-last": isFlipped })}>
          <FeatureList />
        </div>
        {image}
      </div>
    </div>
  );
}

function FeatureItem({ title, description, icon }: { title: string; description: string; icon: string }) {
  return (
    <div className="flex items-start gap-4">
      <div>
        <img src={icon} alt="" className="aspect-square w-10" />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="text-lg text-gray-600">{description}</p>
      </div>
    </div>
  );
}
